import { useEffect, useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { Button } from '../components/ui/button';
import { BarChart3, Users, Clock, CheckCircle, AlertTriangle, Filter, RotateCcw, List, Download } from 'lucide-react';
import api from '../utils/api';
import { toast } from 'sonner';
import { STATUS_COLORS_SOFT as STATUS_COLORS, STATUS_LABELS_SHORT as STATUS_LABELS } from '../utils/constants';

export default function TeamEfficiency() {
  const [report, setReport] = useState([]);
  const [detail, setDetail] = useState([]);
  const [users, setUsers] = useState([]);
  const [clients, setClients] = useState([]);
  const [loading, setLoading] = useState(true);
  const [detailLoading, setDetailLoading] = useState(false);
  const [filters, setFilters] = useState({ member_id: '', client_id: '', status: '', date_from: '', date_to: '' });

  useEffect(() => { fetchInitialData(); }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const fetchInitialData = async () => {
    try {
      const [usersRes, clientsRes] = await Promise.all([api.get('/users'), api.get('/clients')]);
      setUsers(usersRes.data);
      setClients(clientsRes.data);
      await fetchReport({});
    } catch { toast.error('Failed to load data'); }
  };

  const fetchReport = async (params) => {
    setLoading(true);
    try {
      const qp = new URLSearchParams();
      Object.entries(params).forEach(([k, v]) => { if (v) qp.append(k, v); });
      const [summaryRes, detailRes] = await Promise.all([
        api.get(`/reports/team-efficiency?${qp.toString()}`),
        api.get(`/reports/task-time-detail?${qp.toString()}`),
      ]);
      setReport(summaryRes.data);
      setDetail(detailRes.data);
    } catch { toast.error('Failed to load report'); }
    finally { setLoading(false); }
  };

  const handleFilter = () => fetchReport(filters);
  const resetFilters = () => { setFilters({ member_id: '', client_id: '', status: '', date_from: '', date_to: '' }); fetchReport({}); };

  const handleExportExcel = async () => {
    try {
      const qp = new URLSearchParams();
      Object.entries(filters).forEach(([k, v]) => { if (v) qp.append(k, v); });
      const res = await api.get(`/reports/team-efficiency/export?${qp.toString()}`, { responseType: 'blob' });
      const disposition = res.headers?.['content-disposition'] || '';
      const match = disposition.match(/filename="?([^";]+)"?/i);
      const filename = match ? match[1] : 'team_efficiency.xlsx';
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const a = document.createElement('a');
      a.href = url; a.download = filename; document.body.appendChild(a); a.click();
      a.remove(); window.URL.revokeObjectURL(url);
      toast.success('Efficiency report downloaded');
    } catch (e) {
      toast.error('Failed to download report');
    }
  };

  const totals = report.reduce((acc, r) => ({
    assigned: acc.assigned + r.total_assigned,
    completed: acc.completed + r.completed,
    overdue: acc.overdue + r.overdue,
    hours: acc.hours + r.total_hours_logged
  }), { assigned: 0, completed: 0, overdue: 0, hours: 0 });

  // Group detail rows by member
  const detailByMember = {};
  detail.forEach(d => {
    if (!detailByMember[d.member_id]) detailByMember[d.member_id] = { name: d.member_name, rows: [] };
    detailByMember[d.member_id].rows.push(d);
  });

  return (
    <div className="p-8 min-h-full" style={{
        background: 'linear-gradient(135deg, rgba(91, 111, 214, 0.03) 0%, rgba(16, 185, 129, 0.03) 100%)'
      }}>
        <div className="mb-8 flex items-start justify-between gap-4 flex-wrap">
          <div>
            <h1 className="text-4xl font-bold tracking-tight mb-2" style={{
              background: 'linear-gradient(135deg, #1e1b4b 0%, #0f172a 100%)',
              WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent'
            }}>Team Efficiency Report</h1>
            <p className="text-lg text-slate-600">Performance metrics and task-level time breakdown</p>
          </div>
          <Button
            onClick={handleExportExcel}
            className="bg-[#10B981] hover:bg-[#059669] text-white shadow-md h-11 px-5 font-semibold"
            data-testid="export-efficiency-excel-btn"
          >
            <Download className="h-5 w-5 mr-2" /> Download Excel
          </Button>
        </div>

        {/* Summary Cards */}
        <div className="grid grid-cols-4 gap-4 mb-6">
          {[
            { label: 'Total Assigned', value: totals.assigned, icon: Users, color: 'text-[#5B6FD6]', bg: 'bg-[#5B6FD6]/10' },
            { label: 'Completed', value: totals.completed, icon: CheckCircle, color: 'text-[#10B981]', bg: 'bg-[#10B981]/10' },
            { label: 'Overdue', value: totals.overdue, icon: AlertTriangle, color: 'text-red-600', bg: 'bg-red-50' },
            { label: 'Hours Logged', value: totals.hours.toFixed(1), icon: Clock, color: 'text-amber-600', bg: 'bg-amber-50' },
          ].map(card => (
            <Card key={card.label} className="border-slate-200 shadow-sm bg-white/80">
              <CardContent className="p-5 flex items-center gap-4">
                <div className={`h-12 w-12 rounded-lg ${card.bg} flex items-center justify-center`}>
                  <card.icon className={`h-6 w-6 ${card.color}`} />
                </div>
                <div>
                  <p className="text-sm text-slate-500">{card.label}</p>
                  <p className="text-2xl font-bold text-slate-900">{card.value}</p>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>

        {/* Filters */}
        <Card className="border-slate-200 shadow-sm bg-white/80 mb-6" data-testid="efficiency-filters">
          <CardContent className="p-5">
            <div className="flex items-end gap-4 flex-wrap">
              <div className="flex-1 min-w-[150px]">
                <Label className="text-xs text-slate-500">Team Member</Label>
                <Select value={filters.member_id} onValueChange={(v) => setFilters({ ...filters, member_id: v === 'all' ? '' : v })}>
                  <SelectTrigger data-testid="filter-member"><SelectValue placeholder="All Members" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Members</SelectItem>
                    {users.filter(u => u.role !== 'super_admin').map(u => (
                      <SelectItem key={u.id} value={u.id}>{u.full_name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="flex-1 min-w-[150px]">
                <Label className="text-xs text-slate-500">Client</Label>
                <Select value={filters.client_id} onValueChange={(v) => setFilters({ ...filters, client_id: v === 'all' ? '' : v })}>
                  <SelectTrigger data-testid="filter-client"><SelectValue placeholder="All Clients" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Clients</SelectItem>
                    {clients.map(c => (
                      <SelectItem key={c.id} value={c.id}>{c.company}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="flex-1 min-w-[150px]">
                <Label className="text-xs text-slate-500">Status</Label>
                <Select value={filters.status} onValueChange={(v) => setFilters({ ...filters, status: v === 'all' ? '' : v })}>
                  <SelectTrigger data-testid="filter-status"><SelectValue placeholder="All Statuses" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Statuses</SelectItem>
                    <SelectItem value="pending">Pending</SelectItem>
                    <SelectItem value="in_progress">In Progress</SelectItem>
                    <SelectItem value="completed">Completed</SelectItem>
                    <SelectItem value="overdue">Overdue</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="min-w-[140px]">
                <Label className="text-xs text-slate-500">From</Label>
                <Input type="date" value={filters.date_from} onChange={(e) => setFilters({ ...filters, date_from: e.target.value })} data-testid="filter-date-from" />
              </div>
              <div className="min-w-[140px]">
                <Label className="text-xs text-slate-500">To</Label>
                <Input type="date" value={filters.date_to} onChange={(e) => setFilters({ ...filters, date_to: e.target.value })} data-testid="filter-date-to" />
              </div>
              <Button onClick={handleFilter} className="bg-[#5B6FD6] hover:bg-[#4a5bc5]" data-testid="apply-filter-btn">
                <Filter className="h-4 w-4 mr-1" /> Apply
              </Button>
              <Button variant="outline" onClick={resetFilters} data-testid="reset-filter-btn">
                <RotateCcw className="h-4 w-4 mr-1" /> Reset
              </Button>
            </div>
          </CardContent>
        </Card>

        {/* Member Summary Table */}
        <Card className="border-slate-200 shadow-sm bg-white/80 mb-6" data-testid="efficiency-table">
          <CardHeader className="pb-2">
            <CardTitle className="text-xl font-semibold flex items-center gap-2">
              <BarChart3 className="h-5 w-5 text-[#5B6FD6]" />
              Member Summary
              <Badge variant="outline" className="ml-2">{report.length} members</Badge>
            </CardTitle>
          </CardHeader>
          <CardContent className="p-0">
            {loading ? (
              <div className="p-8 text-center text-slate-400">Loading report...</div>
            ) : report.length === 0 ? (
              <div className="p-8 text-center text-slate-400">No data found</div>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full text-sm" data-testid="summary-table">
                  <thead>
                    <tr className="bg-slate-50 border-y border-slate-200">
                      <th className="text-left py-3 px-4 font-semibold text-slate-700">Member</th>
                      <th className="text-center py-3 px-3 font-semibold text-slate-700">Assigned</th>
                      <th className="text-center py-3 px-3 font-semibold text-slate-700">Completed</th>
                      <th className="text-center py-3 px-3 font-semibold text-slate-700">In Progress</th>
                      <th className="text-center py-3 px-3 font-semibold text-slate-700">Overdue</th>
                      <th className="text-center py-3 px-3 font-semibold text-slate-700">Hours</th>
                      <th className="text-center py-3 px-3 font-semibold text-slate-700">Manual %</th>
                      <th className="text-center py-3 px-3 font-semibold text-slate-700">Rate</th>
                    </tr>
                  </thead>
                  <tbody>
                    {report.map(r => (
                      <tr key={r.user_id} className={`border-b border-slate-100 hover:bg-slate-50 ${r.is_flagged ? 'bg-red-50/50' : ''}`} data-testid={`efficiency-row-${r.user_id}`}>
                        <td className="py-3 px-4">
                          <div className="flex items-center gap-2">
                            <div className={`h-8 w-8 rounded-full flex items-center justify-center text-white text-xs font-bold ${r.is_flagged ? 'bg-gradient-to-br from-red-500 to-orange-500' : 'bg-gradient-to-br from-[#5B6FD6] to-[#10B981]'}`}>
                              {r.user_name?.charAt(0)?.toUpperCase()}
                            </div>
                            <div>
                              <p className="font-medium text-slate-900 flex items-center gap-1.5">
                                {r.user_name}
                                {r.is_flagged && <AlertTriangle className="h-3.5 w-3.5 text-red-500" />}
                              </p>
                              <p className="text-xs text-slate-500 capitalize">{r.role}</p>
                            </div>
                          </div>
                        </td>
                        <td className="text-center py-3 px-3 font-medium">{r.total_assigned}</td>
                        <td className="text-center py-3 px-3"><span className="text-green-600 font-medium">{r.completed}</span></td>
                        <td className="text-center py-3 px-3"><span className="text-blue-600 font-medium">{r.in_progress}</span></td>
                        <td className="text-center py-3 px-3">
                          {r.overdue > 0 ? <span className="text-red-600 font-bold">{r.overdue}</span> : <span className="text-slate-400">0</span>}
                        </td>
                        <td className="text-center py-3 px-3">
                          <span className="font-medium text-amber-600">{r.total_hours_logged}h</span>
                          {r.manual_hours > 0 && <span className="text-xs text-slate-400 block">({r.manual_hours}h manual)</span>}
                        </td>
                        <td className="text-center py-3 px-3" data-testid={`manual-pct-${r.user_id}`}>
                          <Badge className={
                            r.manual_pct > 30 ? 'bg-red-100 text-red-700 border-red-200' :
                            r.manual_pct > 15 ? 'bg-amber-100 text-amber-700 border-amber-200' :
                            'bg-slate-100 text-slate-600 border-slate-200'
                          }>
                            {r.manual_pct}%
                          </Badge>
                        </td>
                        <td className="text-center py-3 px-3">
                          <Badge className={r.completion_rate >= 70 ? 'bg-green-100 text-green-700' : r.completion_rate >= 40 ? 'bg-amber-100 text-amber-700' : 'bg-red-100 text-red-700'}>
                            {r.completion_rate}%
                          </Badge>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Detailed Task-Level Time Table */}
        <Card className="border-slate-200 shadow-sm bg-white/80" data-testid="detail-table-card">
          <CardHeader className="pb-2">
            <CardTitle className="text-xl font-semibold flex items-center gap-2">
              <List className="h-5 w-5 text-[#5B6FD6]" />
              Time Logged per Task
              <Badge variant="outline" className="ml-2">{detail.length} entries</Badge>
            </CardTitle>
          </CardHeader>
          <CardContent className="p-0">
            {loading ? (
              <div className="p-8 text-center text-slate-400">Loading...</div>
            ) : detail.length === 0 ? (
              <div className="p-8 text-center text-slate-400">No time entries found</div>
            ) : (
              <div className="overflow-x-auto">
                {Object.entries(detailByMember).map(([memberId, { name, rows }]) => {
                  const memberTotal = rows.reduce((s, r) => s + r.total_hours, 0);
                  return (
                    <div key={memberId} data-testid={`detail-member-${memberId}`}>
                      {/* Member header row */}
                      <div className="bg-[#5B6FD6]/5 border-y border-slate-200 px-4 py-2.5 flex items-center justify-between">
                        <div className="flex items-center gap-2">
                          <div className="h-7 w-7 rounded-full bg-gradient-to-br from-[#5B6FD6] to-[#10B981] flex items-center justify-center text-white text-xs font-bold">
                            {name?.charAt(0)?.toUpperCase()}
                          </div>
                          <span className="font-semibold text-slate-800">{name}</span>
                          <Badge variant="outline" className="text-xs">{rows.length} tasks</Badge>
                        </div>
                        <span className="text-sm font-bold text-amber-600">{memberTotal.toFixed(1)}h total</span>
                      </div>
                      {/* Task rows */}
                      <table className="w-full text-sm">
                        <thead>
                          <tr className="bg-slate-50/50">
                            <th className="text-left py-2 px-4 font-medium text-slate-500 text-xs">Task</th>
                            <th className="text-left py-2 px-3 font-medium text-slate-500 text-xs">Client</th>
                            <th className="text-left py-2 px-3 font-medium text-slate-500 text-xs">Service</th>
                            <th className="text-left py-2 px-3 font-medium text-slate-500 text-xs">Start</th>
                            <th className="text-left py-2 px-3 font-medium text-slate-500 text-xs">Deadline</th>
                            <th className="text-center py-2 px-3 font-medium text-slate-500 text-xs">Status</th>
                            <th className="text-center py-2 px-3 font-medium text-slate-500 text-xs">Timer (h)</th>
                            <th className="text-center py-2 px-3 font-medium text-slate-500 text-xs">Manual (h)</th>
                            <th className="text-center py-2 px-3 font-medium text-slate-500 text-xs">Total (h)</th>
                          </tr>
                        </thead>
                        <tbody>
                          {rows.map(r => (
                            <tr key={r.task_id} className="border-b border-slate-100 hover:bg-slate-50/50" data-testid={`detail-row-${r.task_id}`}>
                              <td className="py-2 px-4 text-slate-800 max-w-[220px] truncate">{r.task_title}</td>
                              <td className="py-2 px-3 text-slate-600 max-w-[140px] truncate">{r.client_name}</td>
                              <td className="py-2 px-3 text-slate-500 text-xs max-w-[120px] truncate">{r.service_name}</td>
                              <td className="py-2 px-3 text-slate-600 text-xs whitespace-nowrap">
                                {r.start_date ? new Date(r.start_date).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: '2-digit' }) : '-'}
                              </td>
                              <td className="py-2 px-3 text-slate-600 text-xs whitespace-nowrap">
                                {r.end_date ? new Date(r.end_date).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: '2-digit' }) : '-'}
                              </td>
                              <td className="text-center py-2 px-3">
                                <Badge className={`text-[10px] ${STATUS_COLORS[r.status] || 'bg-slate-100 text-slate-600'}`}>
                                  {STATUS_LABELS[r.status] || r.status}
                                </Badge>
                              </td>
                              <td className="text-center py-2 px-3 text-slate-600">{r.timer_hours > 0 ? r.timer_hours : '-'}</td>
                              <td className="text-center py-2 px-3">
                                {r.manual_hours > 0 ? <span className="text-amber-600 font-medium">{r.manual_hours}</span> : '-'}
                              </td>
                              <td className="text-center py-2 px-3 font-semibold text-slate-900">{r.total_hours > 0 ? r.total_hours : '-'}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  );
                })}
              </div>
            )}
          </CardContent>
        </Card>
    </div>
  );
}
